import { useState } from 'react';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { PartsItem } from '@/components/sections/service/part-detail/part-item';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { PaginationBar } from '@/components/shared/einstein/pagination-bar';
import { HeadingItem } from '@/components/shared/headline/heading-item';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { styled } from '@/stitches.config';
import { IPartDetail } from '@/types/shared/parts';

const RelatedPartsHeaderStyled = styled(Box, {
  '& .swiper-button-disabled': {
    '&::before': {
      backgroundColor: 'var(--bg-color)',
    },
  },
});

const RelatedPartsStyled = styled(Box, {
  '& .swiper-slide': {
    height: 'auto',
  },
});

interface RelatedPartsProps {
  items: IPartDetail[];
  cardUrl?: string;
  dataTrack?: string;
  dataTest?: string;
  dataTestSection?: string;
}

export const RelatedParts = ({
  items = [],
  cardUrl,
  dataTrack = 'parts-detail-cta',
  dataTest = 'relatedParts',
  dataTestSection = 'section_relatedParts',
}: RelatedPartsProps) => {
  const [showPagination, setShowPagination] = useState<boolean>(true);

  const handleSwiperInit = (swiper: SwiperClass) => {
    if (!swiper) return;
    setShowPagination(!swiper.isLocked);
  };

  return (
    <>
      {items?.length > 0 && (
        <Section size="xl" backgroundColor="gray-light" test={dataTestSection}>
          <Container>
            <RelatedPartsHeaderStyled
              display="flex"
              justifyContent="between"
              alignItems="end"
              marginBottom={{ '@initial': '8', '@sm': '10' }}
            >
              {/* Heading */}
              <HeadingItem title="รายการแนะนำอื่นๆ" />

              {/* Navigation */}
              {items.length > 1 && !!showPagination && (
                <Box
                  display={{
                    '@initial': 'none',
                    '@md': 'flex',
                  }}
                  columnGap="6"
                >
                  <ButtonIcon
                    icon="arrow-back"
                    size="lg"
                    variant="button-icon-color-gray-dark"
                    border
                    tracking={{
                      dataTrack,
                      dataTrackSection: 'related-parts',
                      dataTrackValue: 'Prev',
                    }}
                    test={{
                      dataTest: `${dataTest}_prev_button`,
                    }}
                    className="button-related-parts-prev"
                  />
                  <ButtonIcon
                    icon="arrow-forward"
                    size="lg"
                    variant="button-icon-color-gray-dark"
                    border
                    tracking={{
                      dataTrack,
                      dataTrackSection: 'related-parts',
                      dataTrackValue: 'Next',
                    }}
                    test={{
                      dataTest: `${dataTest}_next_button`,
                    }}
                    className="button-related-parts-next"
                  />
                </Box>
              )}
            </RelatedPartsHeaderStyled>

            {/* Swiper for Related Parts */}
            <RelatedPartsStyled position="relative" width="full">
              <Swiper
                modules={[Keyboard, Pagination, Navigation]}
                threshold={10}
                slidesPerView={2}
                spaceBetween={24}
                loop={false}
                allowTouchMove={items.length > 1}
                keyboard={{
                  enabled: true,
                  onlyInViewport: true,
                  pageUpDown: true,
                }}
                breakpoints={{
                  '768': {
                    slidesPerView: 3,
                  },
                  '1280': {
                    slidesPerView: 4,
                  },
                }}
                navigation={{
                  prevEl: `.button-related-parts-prev`,
                  nextEl: `.button-related-parts-next`,
                }}
                pagination={{
                  el: `.pagination-related-parts`,
                  clickable: true,
                  renderBullet: function (index, className) {
                    return `<div class="${className}" data-test="pagination_${
                      index + 1
                    }"><span>${index + 1}</span></div>`;
                  },
                }}
                onInit={handleSwiperInit}
                onResize={handleSwiperInit}
              >
                {items.map((item, index) => (
                  <SwiperSlide key={index}>
                    <PartsItem
                      part={item}
                      index={index}
                      dataTest={`${dataTest}_item_${index + 1}`}
                      dataTrack={dataTrack}
                      dataTrackSection="related-parts"
                      cardUrl={cardUrl}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </RelatedPartsStyled>
          </Container>

          {/* Pagination */}
          {items.length > 1 && !!showPagination && (
            <Box marginTop={{ '@initial': '8', '@sm': '10' }}>
              <PaginationBar justify="center" variant="gray" isStatic>
                <Container>
                  <div
                    className={`swiper-pagination pagination-related-parts`}
                    data-test="paginations"
                  />
                </Container>
              </PaginationBar>
            </Box>
          )}
        </Section>
      )}
    </>
  );
};
RelatedParts.displayName = 'RelatedParts';
